// src/state.js — Shared mutable state object
// All modules import this and read/write via property access: state.isRecording = true
export const state = {
    // Auth
    currentUser: null,
    idToken: null,
    isAdmin: false,
    // Recording
    mediaRecorder: null,
    audioChunks: [],
    isRecording: false,
    isPaused: false,
    recordingStartInProgress: false,
    timerInterval: null,
    startTime: null,
    startTimeMono: null,
    elapsedBeforePause: 0,
    wakeLock: null,
    currentNoteContent: '',
    recordingSegments: [],
    recordingInterrupted: false,
    pendingUploadBlob: null,
    pendingUploadSource: null,
    pendingUploadMeta: null,
    lastProcessingJobId: null,
    recorderGeneration: 0,
    recordingSegmentMeta: [],
    lastSegmentJoinInfo: null,
    // Session/IndexedDB
    currentSessionId: null,
    currentSessionDbId: null,
    autoSaveInterval: null,
    resumingSessionId: null,
    // QR
    pendingQrSession: null,
    qrPollInterval: null,
    qrTimerInterval: null,
    qrSessionId: null,
    // Mode
    recordingMode: 'audio-upload',
    noteMode: 'appointment',
    // Profile/Inactivity
    inactivityTimer: null,
    lastInteractionTime: Date.now(),
    _sidebarNavInitialized: false,
    // Admin
    adminFeedbackTickets: [],
    adminFeedbackFilter: 'all',
    adminUsersCache: [],
    adminPendingCache: [],
    // Legacy
    currentIndexedDBId: null,
    // Notes
    notesPageSize: 20,
    notesOffset: 0,
    notesTotalCount: 0,
    allLoadedNotes: [],
    _lastLoadNotesTime: 0,
    noteAudioPlayer: null,
    noteAudioObjectUrl: null,
    noteAudioAnimFrame: null,
    // Admin usage
    adminNotesPageSize: 25,
    adminNotesOffset: 0,
    adminNotesTotal: 0,
    adminDateFrom: '',
    adminDateTo: '',
    adminUsageLoading: false,
    adminOfficeFilter: '',
    adminProfileFilter: '',
    adminUserVisibility: null, // initialized at runtime
    adminTestHashes: [],       // initialized at runtime
    // QR scanner
    html5QrScanner: null,
    inviteTimerInterval: null,
    // Diagnostics
    _lastDiagnosticSentAt: 0,
    // Feedback
    _feedbackScreenshots: [],
    _feedbackLongPressTimer: null,
    _feedbackLongPressTriggered: false,
    // Profiles modal
    pinTargetProfile: null,
    editingProfileId: null,
    resetPinProfileId: null,
    resetPinOldVerified: false,
    // Analytics
    analyticsData: null,
    analyticsCharts: {},
    analyticsViewAsHash: null,
    analyticsLastLoadTime: null,
    analyticsFeatureFlags: null,
    analyticsAutoRefreshTimer: null,
    // Appointment/Feedback
    selectedAppointmentType: 'other_general',
    currentFeedbackNoteId: null,
    currentFeedbackRating: null,
    analyticsDateRange: '30days',
    // APPOINTMENT_TYPES (mutable because it can be loaded from server)
    APPOINTMENT_TYPES: [
        { key: 'other_general', label: 'Other / General SOAP', shortLabel: 'Other / General', category: 'General' },
        { key: 'hygiene_recall', label: 'Periodic / Hygiene Recall', shortLabel: 'Hygiene / Recall', category: 'Exams' },
        { key: 'new_patient_exam', label: 'New Patient Exam', shortLabel: 'New Patient Exam', category: 'Exams' },
        { key: 'comprehensive_exam', label: 'Comprehensive Exam', shortLabel: 'Comprehensive Exam', category: 'Exams' },
        { key: 'filling_restoration', label: 'Filling / Restoration', shortLabel: 'Filling / Restoration', category: 'Procedures' },
        { key: 'emergency_visit', label: 'Emergency Visit', shortLabel: 'Emergency Visit', category: 'Exams' },
        { key: 'crown_bridge', label: 'Crown / Bridge', shortLabel: 'Crown / Bridge', category: 'Procedures' },
        { key: 'root_canal', label: 'Root Canal', shortLabel: 'Root Canal', category: 'Procedures' },
        { key: 'extraction', label: 'Extraction', shortLabel: 'Extraction', category: 'Procedures' },
        { key: 'implant_consult', label: 'Implant Consult', shortLabel: 'Implant Consult', category: 'Specialty' },
        { key: 'periodontal_visit', label: 'Periodontal Visit', shortLabel: 'Periodontal Visit', category: 'Specialty' },
        { key: 'ortho_consult', label: 'Ortho Consult', shortLabel: 'Ortho Consult', category: 'Specialty' },
        { key: 'denture_prosthodontic', label: 'Denture / Prosthodontic', shortLabel: 'Denture / Prostho.', category: 'Specialty' },
    ],
};

// Runtime initialization for localStorage-dependent state
try {
    state.adminUserVisibility = localStorage.getItem('dentscribe_admin_visibility') || 'real';
} catch (e) {
    state.adminUserVisibility = 'real';
}
try {
    state.adminTestHashes = JSON.parse(localStorage.getItem('dentscribe_admin_testHashes') || '[]');
} catch (e) {
    state.adminTestHashes = [];
}
